import { useMemo, SyntheticEvent, useRef } from "react";
import { displayContractBasis } from "@/utils/contractBasis";
import { Autocomplete, Stack, TextField } from "@mui/material";
import PolicyIcon from "@mui/icons-material/Policy";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";

import {
  FilterList,
  FilterListItem,
  FilterLiveForm,
  FilterLiveSearch,
  SavedQueriesList,
  useGetList,
  useGetMany,
} from "react-admin";
import { Controller, useFormContext } from "react-hook-form";
import { StyledListAside } from "@/components/ui/react-admin/StyledListAside";
import { Policy, ResourceName, ValidAny, type Vendor } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

// ─── Hook: extract unique filter values from all policies ────────────────────

const usePolicyFilterOptions = () => {
  const { data: policies } = useGetList<Policy>(ResourceName.POLICIES, {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "effective_start_date", order: "DESC" },
  });

  return useMemo(() => {
    if (!policies)
      return {
        statuses: [],
        contractBases: [],
        clientIds: new Set<string>(),
        vendorIds: new Set<string>(),
        tpaVendorIds: new Set<string>(),
        rxVendorIds: new Set<string>(),
      };

    const statuses = [...new Set(policies.map((p) => p.status).filter(Boolean))];
    const contractBases = [...new Set(policies.map((p) => p.contract_basis).filter(Boolean))].sort();
    const clientIds = new Set(
      policies
        .map((p) => p.client_id_ref)
        .filter(Boolean)
        .map(String)
    );
    const vendorIds = new Set(
      policies
        .map((p) => p.vendor_id_ref)
        .filter(Boolean)
        .map(String)
    );
    const tpaVendorIds = new Set(policies.flatMap((p) => p.tpa_vendor_ids ?? []).map(String));
    const rxVendorIds = new Set(policies.flatMap((p) => p.rx_vendor_ids ?? []).map(String));

    return { statuses, contractBases, clientIds, vendorIds, tpaVendorIds, rxVendorIds };
  }, [policies]);
};

// ─── Autocomplete filter inputs ──────────────────────────────────────────────

interface ReferenceFilterProps {
  clientIds: Set<string>;
  vendorIds: Set<string>;
  tpaVendorIds: Set<string>;
  rxVendorIds: Set<string>;
}

const ClientFilterInput = ({ clientIds }: { clientIds: Set<string> }) => {
  const { control } = useFormContext();
  const hasInitialized = useRef(false);

  const { data: allClients, isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  // Only show clients that have policies
  const options = useMemo(() => (allClients || []).filter((c) => clientIds.has(String(c.id))), [allClients, clientIds]);

  if (!isLoading && options.length > 0) {
    hasInitialized.current = true;
  }

  return (
    <Controller
      name="client_id_ref"
      control={control}
      render={({ field }) => {
        // Multi-select: filter matches policies for ANY selected client. Accepts
        // a legacy scalar value (e.g. an older saved query) by normalizing to array.
        const selectedIds = Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];
        return (
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={(option) => option?.client_legal_name || ""}
            value={hasInitialized.current ? options.filter((c) => selectedIds.includes(c.id)) : []}
            onChange={(_event: SyntheticEvent, newValue: ValidAny) => {
              field.onChange(newValue.length ? newValue.map((v: ValidAny) => v.id) : null);
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={isLoading}
            size="small"
            renderInput={(params) => <TextField {...params} label="Client" variant="outlined" />}
            fullWidth
            clearOnEscape
          />
        );
      }}
    />
  );
};

const CarrierFilterInput = ({ vendorIds }: { vendorIds: Set<string> }) => {
  const { control } = useFormContext();
  const hasInitialized = useRef(false);

  const { data: allCarriers, isLoading } = useGetList("vendors/carriers", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "company_name", order: "ASC" },
  });

  // Only show carriers that have policies
  const options = useMemo(
    () => (allCarriers || []).filter((c) => vendorIds.has(String(c.id))),
    [allCarriers, vendorIds]
  );

  if (!isLoading && options.length > 0) {
    hasInitialized.current = true;
  }

  return (
    <Controller
      name="vendor_id_ref"
      control={control}
      render={({ field }) => {
        // Multi-select: filter matches policies for ANY selected carrier.
        const selectedIds = Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];
        return (
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={(option) => option?.company_name || ""}
            value={hasInitialized.current ? options.filter((c) => selectedIds.includes(c.id)) : []}
            onChange={(_event: SyntheticEvent, newValue: ValidAny) => {
              field.onChange(newValue.length ? newValue.map((v: ValidAny) => v.id) : null);
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={isLoading}
            size="small"
            renderInput={(params) => <TextField {...params} label="Carrier" variant="outlined" />}
            fullWidth
            clearOnEscape
          />
        );
      }}
    />
  );
};

/**
 * Multi-select filter over a junction-table vendor role (TPA or Rx/PBM). Options
 * are the vendors referenced by the loaded policies, with names resolved via
 * useGetMany. `source` is the list/backend filter key (tpa_vendor_id / rx_vendor_id).
 */
const JunctionVendorFilterInput = ({
  source,
  label,
  vendorIds,
}: {
  source: string;
  label: string;
  vendorIds: Set<string>;
}) => {
  const { control } = useFormContext();
  const hasInitialized = useRef(false);

  const ids = useMemo(() => [...vendorIds], [vendorIds]);
  const { data: vendors, isLoading } = useGetMany<Vendor>(ResourceName.VENDORS, { ids }, { enabled: ids.length > 0 });

  const options = useMemo(
    () => (vendors || []).slice().sort((a, b) => (a.company_name || "").localeCompare(b.company_name || "")),
    [vendors]
  );

  if (!isLoading && options.length > 0) {
    hasInitialized.current = true;
  }

  return (
    <Controller
      name={source}
      control={control}
      render={({ field }) => {
        // Multi-select: filter matches policies linked to ANY selected vendor.
        const selectedIds = Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];
        return (
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={(option) => option?.company_name || ""}
            value={hasInitialized.current ? options.filter((c) => selectedIds.includes(String(c.id))) : []}
            onChange={(_event: SyntheticEvent, newValue: ValidAny) => {
              field.onChange(newValue.length ? newValue.map((v: ValidAny) => String(v.id)) : null);
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={isLoading}
            size="small"
            renderInput={(params) => <TextField {...params} label={label} variant="outlined" />}
            fullWidth
            clearOnEscape
          />
        );
      }}
    />
  );
};

const ReferenceFilters = ({ clientIds, vendorIds, tpaVendorIds, rxVendorIds }: ReferenceFilterProps) => {
  return (
    <Stack spacing={2}>
      <ClientFilterInput clientIds={clientIds} />
      <CarrierFilterInput vendorIds={vendorIds} />
      {tpaVendorIds.size > 0 && (
        <JunctionVendorFilterInput source="tpa_vendor_id" label="TPA" vendorIds={tpaVendorIds} />
      )}
      {rxVendorIds.size > 0 && (
        <JunctionVendorFilterInput source="rx_vendor_id" label="Rx/PBM" vendorIds={rxVendorIds} />
      )}
    </Stack>
  );
};

// ─── Main aside ──────────────────────────────────────────────────────────────

export const PolicyListAside = () => {
  const { statuses, contractBases, clientIds, vendorIds, tpaVendorIds, rxVendorIds } = usePolicyFilterOptions();

  return (
    <StyledListAside>
      <FilterLiveSearch />
      <SavedQueriesList />

      <FilterLiveForm>
        <ReferenceFilters
          clientIds={clientIds}
          vendorIds={vendorIds}
          tpaVendorIds={tpaVendorIds}
          rxVendorIds={rxVendorIds}
        />
      </FilterLiveForm>

      {statuses.length > 0 && (
        <FilterList label="Policy Status" icon={<PolicyIcon />}>
          {statuses.map((status) => (
            <FilterListItem key={status} label={formatToTitleCase(status)} value={{ status }} />
          ))}
        </FilterList>
      )}

      {/* Benefit presence — also the destination of the dashboard average-card drill-throughs. */}
      <FilterList label="Benefit Type" icon={<LayersOutlinedIcon />}>
        <FilterListItem label="Has Specific Benefit" value={{ has_specific_benefit: true }} />
        <FilterListItem label="Has Aggregate Benefit" value={{ has_aggregate_benefit: true }} />
      </FilterList>

      <FilterList label="Specific Deductible" icon={<AttachMoneyIcon />}>
        <FilterListItem label="Under $100k" value={{ specific_deductible_gte: 0, specific_deductible_lte: 100000 }} />
        <FilterListItem
          label="$100–$200k"
          value={{ specific_deductible_gte: 100000, specific_deductible_lte: 200000 }}
        />
        <FilterListItem
          label="$200–$300k"
          value={{ specific_deductible_gte: 200000, specific_deductible_lte: 300000 }}
        />
        <FilterListItem
          label="$300–$500k"
          value={{ specific_deductible_gte: 300000, specific_deductible_lte: 500000 }}
        />
        <FilterListItem
          label="$500k+"
          value={{ specific_deductible_gte: 500000, specific_deductible_lte: 1000000000 }}
        />
      </FilterList>

      {contractBases.length > 0 && (
        <FilterList label="Contract Basis" icon={<DescriptionOutlinedIcon />}>
          {contractBases.map((basis) => (
            <FilterListItem key={basis} label={displayContractBasis(basis)} value={{ contract_basis: basis }} />
          ))}
        </FilterList>
      )}
    </StyledListAside>
  );
};
